import { captureAndReportError } from './errorReporting';
import { safeLocalStorage } from './safeLocalStorage';
import type { S3Config } from './upload-providers/S3Provider';
import type { WalletAdapter } from './upload-providers/types';
import { normalizeSignedMessageResult } from './wallet';

const STORAGE_KEY = 's3_encrypted_config';
const SALT_KEY = 's3_encryption_salt';
const PUBLIC_CONFIG_KEY = 's3_public_config';

const sessionConfigCache = new Map<string, S3Config>();

export interface S3PublicConfig {
  bucketName: string;
  region?: string;
  pathPrefix?: string;
  cloudFrontDomain?: string;
}

async function deriveEncryptionKey(
  walletAdapter: WalletAdapter | null,
  salt: Uint8Array,
): Promise<CryptoKey> {
  if (!walletAdapter) {
    const fallbackKey = safeLocalStorage.getItem('s3_fallback_key');
    if (!fallbackKey) {
      const newKey = crypto.getRandomValues(new Uint8Array(32));
      safeLocalStorage.setItem(
        's3_fallback_key',
        btoa(String.fromCharCode(...Array.from(newKey))),
      );
      return crypto.subtle.importKey(
        'raw',
        newKey,
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt'],
      );
    }

    const keyData = Uint8Array.from(atob(fallbackKey), (c) => c.charCodeAt(0));
    return crypto.subtle.importKey('raw', keyData, { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt',
    ]);
  }

  const message = new TextEncoder().encode(
    'Sign this message to decrypt your S3 credentials to upload files for the Publishing Portal. This signature will not be sent to any server.',
  );

  const signature = normalizeSignedMessageResult(
    await walletAdapter.signMessage(message),
  );

  const signatureBuffer = new Uint8Array(signature).buffer;
  const saltBuffer = new Uint8Array(salt).buffer;

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    signatureBuffer,
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptS3Config(
  config: S3Config,
  walletAdapter: WalletAdapter | null,
): Promise<void> {
  try {
    let salt: Uint8Array;
    const storedSalt = safeLocalStorage.getItem(SALT_KEY);
    if (storedSalt) {
      salt = Uint8Array.from(atob(storedSalt), (c) => c.charCodeAt(0));
    } else {
      salt = crypto.getRandomValues(new Uint8Array(16));
      safeLocalStorage.setItem(
        SALT_KEY,
        btoa(String.fromCharCode(...Array.from(salt))),
      );
    }

    const key = await deriveEncryptionKey(walletAdapter, salt);

    const configString = JSON.stringify(config);
    const encodedConfig = new TextEncoder().encode(configString);

    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encryptedData = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      encodedConfig,
    );

    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedData), iv.length);

    const encryptedBase64 = btoa(String.fromCharCode(...Array.from(combined)));
    safeLocalStorage.setItem(STORAGE_KEY, encryptedBase64);

    const publicConfig: S3PublicConfig = {
      bucketName: config.bucketName,
      region: config.region,
      pathPrefix: config.pathPrefix,
      cloudFrontDomain: config.cloudFrontDomain,
    };
    safeLocalStorage.setItem(PUBLIC_CONFIG_KEY, JSON.stringify(publicConfig));
  } catch (error) {
    captureAndReportError('Failed to encrypt S3 configuration:', error);
    throw new Error('Failed to encrypt S3 credentials');
  }
}

export async function decryptS3Config(
  walletAdapter: WalletAdapter | null,
): Promise<S3Config | null> {
  try {
    const walletKey = walletAdapter?.publicKey?.toBase58?.() || 'fallback';
    const cachedConfig = sessionConfigCache.get(walletKey);
    if (cachedConfig) {
      return cachedConfig;
    }

    const encryptedBase64 = safeLocalStorage.getItem(STORAGE_KEY);
    const storedSalt = safeLocalStorage.getItem(SALT_KEY);

    if (!encryptedBase64 || !storedSalt) {
      return null;
    }

    const salt = Uint8Array.from(atob(storedSalt), (c) => c.charCodeAt(0));

    const key = await deriveEncryptionKey(walletAdapter, salt);

    const combined = Uint8Array.from(atob(encryptedBase64), (c) =>
      c.charCodeAt(0),
    );

    const iv = combined.slice(0, 12);
    const encryptedData = combined.slice(12);

    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      encryptedData,
    );

    const decoder = new TextDecoder();
    const configString = decoder.decode(decryptedData);
    const config = JSON.parse(configString) as S3Config;

    sessionConfigCache.set(walletKey, config);

    return config;
  } catch (error) {
    captureAndReportError('Failed to decrypt S3 configuration:', error);
    clearS3Config();
    return null;
  }
}

export function getS3PublicConfig(): S3PublicConfig | null {
  try {
    const stored = safeLocalStorage.getItem(PUBLIC_CONFIG_KEY);
    if (stored) {
      return JSON.parse(stored) as S3PublicConfig;
    }
  } catch (e) {
    captureAndReportError('Failed to load public S3 config:', e);
  }
  return null;
}

export function clearS3Config(): void {
  safeLocalStorage.removeItem(STORAGE_KEY);
  safeLocalStorage.removeItem(SALT_KEY);
  safeLocalStorage.removeItem('s3_fallback_key');
  safeLocalStorage.removeItem(PUBLIC_CONFIG_KEY);

  sessionConfigCache.clear();
}

export function hasS3Config(): boolean {
  return !!safeLocalStorage.getItem(STORAGE_KEY);
}

export function maskS3Config(config: S3Config): Partial<S3Config> {
  return {
    accessKeyId: `${config.accessKeyId.substring(0, 4)}****`,
    secretAccessKey: '****',
    bucketName: config.bucketName,
    region: config.region,
    pathPrefix: config.pathPrefix,
    cloudFrontDomain: config.cloudFrontDomain,
  };
}
